// src/pages/HistoryDetail.jsx
import React, { useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { FaChevronLeft, FaHistory, FaQuoteRight } from "react-icons/fa";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { historyMilestones, cn } from "./historyData";

const HistoryDetail = () => {
  const { year } = useParams();
  const navigate = useNavigate();
  const milestone = historyMilestones.find((m) => m.year === year);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  if (!milestone) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50">
        <h1 className="text-2xl font-bold mb-4">Milestone Not Found</h1>
        <Link to="/utah-at-a-glance/utah-history" className="text-blue-600 hover:underline">
          Back to Timeline
        </Link>
      </div>
    );
  }

  const isDark = ["space-age", "modern-tech", "ultra-modern"].includes(milestone.theme);

  return (
    <div className={cn(
      "min-h-screen transition-colors duration-1000",
      milestone.theme === "vintage" || milestone.theme === "classic" ? "bg-[#FDF6E3]" : 
      milestone.theme === "mid-century" || milestone.theme === "modernist" ? "bg-slate-50" :
      milestone.theme === "modern" ? "bg-rose-50" : "bg-[#0f172a]"
    )}>
      <Header />

      <main className="max-w-4xl mx-auto px-4 py-24">

        {/* Hero Section */}
        <section className="mb-16">
          <div className="flex items-center gap-6 mb-8">
            <span className={cn(
              "px-4 py-1 rounded-full text-sm font-bold border",
              isDark ? "bg-blue-500/10 border-blue-400/30 text-blue-400" : "bg-blue-50 border-blue-200 text-blue-600"
            )}>
              {milestone.category}
            </span>
            <span className={cn("text-3xl font-black", isDark ? "text-white" : "text-blue-900")}>
              {milestone.year}
            </span>
          </div>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className={cn(
              "text-5xl md:text-7xl font-bold mb-8 leading-tight",
              milestone.theme === "vintage" || milestone.theme === "classic" ? "font-serif text-amber-950" : isDark ? "text-white" : "text-blue-950"
            )}
          >
            {milestone.title}
          </motion.h1>

          <motion.div 
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className={cn(
              "mb-12 rounded-3xl overflow-hidden shadow-2xl border-2",
              isDark ? "border-blue-500/30" : "border-white"
            )}
          >
            <img 
              src={milestone.image} 
              alt={milestone.title}
              className="w-full aspect-video object-cover"
            />
          </motion.div>
        </section>

        {/* Content Section */}
        <div className="grid gap-16">
          {/* Main Story */}
          <motion.section
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="prose prose-lg max-w-none"
          >
            <h2 className={cn("text-2xl font-bold mb-6", isDark ? "text-blue-300" : "text-blue-800")}>
              The Full Story
            </h2>
            <p className={cn(
              "text-xl leading-relaxed",
              isDark ? "text-slate-100" : "text-slate-900",
              (milestone.theme === "vintage" || milestone.theme === "classic") && "font-serif"
            )}>
              {milestone.fullStory}
            </p>
          </motion.section>

          {/* Legacy Box */}
          <motion.section
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className={cn(
              "p-8 md:p-12 rounded-3xl border relative overflow-hidden",
              milestone.theme === "vintage" ? "bg-amber-50 border-amber-200" : 
              isDark ? "bg-blue-600/10 border-blue-500/30" : "bg-white border-gray-100 shadow-xl"
            )}
          >
            <FaQuoteRight className={cn("absolute top-8 right-8 text-4xl opacity-10", isDark ? "text-blue-400" : "text-blue-600")} />
            <h2 className={cn("text-2xl font-bold mb-6", isDark ? "text-white" : "text-blue-900")}>
              Lasting Legacy
            </h2>
            <p className={cn(
              "text-lg leading-relaxed italic",
              isDark ? "text-slate-200" : "text-slate-900"
            )}>
              {milestone.legacy}
            </p>
          </motion.section>

        </div>
      </main>

      <Footer />
    </div>
  );
};

export default HistoryDetail;
